import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { chartsGridClasses } from '@mui/x-charts/ChartsGrid';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { PAYMENT_FORECAST_HOME_KEY, PaymentForecastBasis } from 'shared';

import { Card } from "../../../../common/Atoms/Card";
import { MultiButtonCardHeader } from "../../../../common/Atoms/MultiButtonCardHeader";
import { Select } from '../../../../common/Atoms/Select';
import { Button } from '../../../../common/Atoms/Button';
import { formatAmount } from '../../../../common/lib/helpers';
import { useFetchPaymentForecastMetricsQuery } from '../../../services/api/organisationApi/organisation';
import { LoadingOverlay } from '../../Molecules/LoadingOverlay';

const periodOptions = [7, 14, 30, 60, 90].map(value => ({ value, label: `${value} days` }));

const barColor = `#3fa392`;
const lateColor = `#e9a23b`;
const lateLabel = `Late`;

export function PaymentForecast() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const [days, setDays] = useState(30);
  const [basis, setBasis] = useState<PaymentForecastBasis>(`expected`);
  const [currency, setCurrency] = useState<string>(PAYMENT_FORECAST_HOME_KEY);

  const { data, isLoading } = useFetchPaymentForecastMetricsQuery(
    { orgId: organisationId, days, basis },
    { skip: !organisationId, refetchOnMountOrArgChange: true },
  );

  const homeCurrency = data?.homeCurrency || `USD`;
  const invoiceCurrencies = data?.currencies || [];
  // Only offer a currency picker when the window actually spans more than one currency
  const showCurrencyPicker = invoiceCurrencies.length > 1 || (invoiceCurrencies.length === 1 && invoiceCurrencies[0] !== homeCurrency);
  const selectedCurrency = showCurrencyPicker && invoiceCurrencies.includes(currency) ? currency : PAYMENT_FORECAST_HOME_KEY;
  const displayCurrency = selectedCurrency === PAYMENT_FORECAST_HOME_KEY ? homeCurrency : selectedCurrency;

  const currencyOptions = useMemo(() => [
    { value: PAYMENT_FORECAST_HOME_KEY, label: `${homeCurrency} (home)` },
    ...invoiceCurrencies.map(c => ({ value: c, label: c })),
  ], [homeCurrency, invoiceCurrencies]);

  const dataset = useMemo(() => {
    const buckets = data?.late?.invoiceCount
      ? [{ ...data.late, label: lateLabel }, ...data.buckets]
      : data?.buckets || [];

    return buckets.map(b => ({
      label: b.label,
      amount: (b.amounts?.[selectedCurrency] || 0) / 100,
      count: b.invoiceCount,
    }));
  }, [data, selectedCurrency]);

  const totalCents = data?.totals?.amounts?.[selectedCurrency] || 0;
  const format = (cents: number) => formatAmount(cents, displayCurrency);

  const tiles = [
    { label: basis === `expected` ? `Expected in` : `Due in`, value: format(totalCents) },
    { label: `Invoices`, value: String(data?.totals?.invoiceCount || 0) },
    basis === `expected`
      ? { label: `Already overdue`, value: String(data?.totals?.overdueCount || 0) }
      : { label: `Predicted late`, value: String(data?.totals?.predictedLateCount || 0) },
  ];

  return (
    <LoadingOverlay loading={ isLoading }
      className={ `h-full` }>
      <Card className={ `h-full min-h-fit` }
        variant={ `soft` }>
        <MultiButtonCardHeader
          title={ `Expected Payments` }
          description={
            basis === `expected`
              ? `Unpaid invoices by predicted payment date (due date + customer's average days to pay)`
              : `Unpaid invoices by due date in the next ${days} days`
          }
          variant={ `plain` }
          cta={
            <div className={ `flex gap-2 shrink-0 ml-4 mt-2` }>
              { showCurrencyPicker && (
                <div className={ `w-32` }>
                  <Select
                    options={ currencyOptions }
                    onChange={ value => setCurrency(value.value) }
                    selected={ selectedCurrency }
                  />
                </div>
              ) }
              <div className={ `w-32` }>
                <Select
                  options={ periodOptions }
                  onChange={ value => setDays(value.value) }
                  selected={ days }
                />
              </div>
              <div className={ `flex` }>
                <Button
                  className={ `rounded-tr-none rounded-br-none rounded-tl-md rounded-bl-md focus:ring-0` }
                  color={ basis === `expected` ? `paidniceGreen` : `paidniceGreenOutline` }
                  onClick={ () => setBasis(`expected`) }
                >
                  { `Expected` }
                </Button>
                <Button
                  className={ `rounded-tl-none rounded-bl-none rounded-tr-md rounded-br-md focus:ring-0` }
                  color={ basis === `due` ? `paidniceGreen` : `paidniceGreenOutline` }
                  onClick={ () => setBasis(`due`) }
                >
                  { `Due` }
                </Button>
              </div>
            </div>
          }
        />
        <div className={ `mt-4 -mx-4 sm:-mx-5 grid grid-cols-3 border-y border-gray-100 bg-[#fafbfc] dark:bg-gray-50` }>
          { tiles.map((tile, i) => (
            <div key={ tile.label }
              className={ `py-3 px-4 sm:px-5 ${i < tiles.length - 1 ? `border-r border-gray-100` : ``}` }>
              <div className={ `text-[11px] font-semibold tracking-wider text-gray-400 uppercase` }>
                { tile.label }
                { i === 0 ? ` ${days} days` : `` }
              </div>
              <div className={ `text-lg font-semibold mt-0.5 tabular-nums` }>{ tile.value }</div>
            </div>
          )) }
        </div>
        <BarChart
          dataset={ dataset }
          xAxis={ [{
            scaleType: `band`,
            dataKey: `label`,
            disableTicks: true,
            tickLabelStyle: { fill: `#6b7c87`, fontSize: 12 },
            colorMap: { type: `ordinal`, values: [lateLabel], colors: [lateColor], unknownColor: barColor },
          }] }
          yAxis={ [{ position: `none`, tickNumber: 3 }] }
          series={ [{
            dataKey: `amount`,
            label: basis === `expected` ? `Expected` : `Due`,
            color: barColor,
            valueFormatter: (value: number | null, { dataIndex }) => {
              const count = dataset[dataIndex]?.count || 0;

              return `${format(Math.round((value || 0) * 100))} · ${count} ${count === 1 ? `invoice` : `invoices`}`;
            },
          }] }
          grid={ { horizontal: true } }
          borderRadius={ 5 }
          hideLegend
          height={ 260 }
          margin={ { left: 0, right: 0, top: 16, bottom: 0 } }
          sx={ {
            [`.${chartsGridClasses.line}`]: { stroke: `#eef1f4` },
            [`.${axisClasses.line}`]: { stroke: `#e6eaee` },
          } }
        />
      </Card>
    </LoadingOverlay>
  );
}
